import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatCheckboxDefaultOptions, MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import type { OrgCreateData } from '@dashboard/app/interfaces/sign-up/org-create-data';
import type { SignupOrg } from '@dashboard/app/interfaces/sign-up/signup-orgs-response';
import type { CountryData } from '@go-modules/interfaces/country-data';
import { NgxCountryService } from '@go-modules/services/country/country.service';
import { NoWhitespaceValidator } from '@go-modules/validators/no-whitespace';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import type { ConfirmDialogData } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';
import type { OrgTypeResource } from '@go-modules/interfaces/org-type-resource';
import { OrgTypesService } from '@go-modules/services/org-types/org-types.service';

@Component({
	selector: 'org-create',
	templateUrl: './org-create.component.html',
	styleUrls: ['./org-create.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{
		provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
		useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions
	}, TranslatePipe],
	standalone: false
})
export class OrgCreateComponent implements OnChanges, OnInit {
	@Input()
	public email: string;

	@Input()
	public unallowedDomain: boolean;

	@Input()
	public orgs: SignupOrg[] = [];

	@Input()
	public set defaultName (value: string) {
		this.form.controls.name.setValue(value);
	}

	@Output()
	public onComplete = new EventEmitter<OrgCreateData>(); // eslint-disable-line @angular-eslint/no-output-on-prefix

	@Output()
	public onBack = new EventEmitter<void>(); // eslint-disable-line @angular-eslint/no-output-on-prefix

	public form = new FormGroup({
		type: new FormControl<OrgTypeResource|null>(null, [Validators.required]),
		name: new FormControl<string>(null, [
			Validators.required,
			NoWhitespaceValidator,
			this.orgNameUniqueValidator(),
			Validators.maxLength(60)
		]),
		country: new FormControl<string>(null, [Validators.required]),
		orgJoinViaEmailDomain: new FormControl(true)
	});

	public countries: CountryData[] = [];
	public domain: string = '';
	public orgJoinViaEmailDomainConfirmDialogFlag: boolean = false;
	public orgTypes$: Observable<OrgTypeResource[]>;

	constructor (
		private countryService: NgxCountryService,
		private orgTypesService: OrgTypesService,
		private dialog: MatDialog,
		private translatePipe: TranslatePipe
	) {}

	public ngOnInit (): void {
		this.form.controls.country.setValidators(Validators.required);
		this.countryService.getCountries()
			.subscribe((countries) => this.countries = countries);
		this.orgTypes$ = this.orgTypesService.getOrgTypes().pipe(
			map((orgTypes) => orgTypes.sort((a, b) => a.sort_order - b.sort_order))
		);
	}

	public ngOnChanges (changes: SimpleChanges) {
		if (changes.email) {
			this.domain = `@${changes.email.currentValue?.split('@').pop()}`;
		}
	}

	public orgNameUniqueValidator (): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;

			if (!value) {
				return null;
			}

			const exists = this.orgs.find((org) => {
				return org.name.toLowerCase() === value.toLowerCase();
			});

			if (exists) {

				return { orgNameUniqueValidator: 'account-setup-error-org-name-exists' };
			}

			return null;
		};
	}

	public submit () {
		if (this.form.valid) {
			const allowedDomains = [];
			const { value: checked } = this.form.get('orgJoinViaEmailDomain');

			if (checked && !this.unallowedDomain) {
				allowedDomains.push(this.domain.split('@').pop());
			}

			this.onComplete.emit({
				name: this.form.controls.name.value,
				orgSettings: {
					org_type_id: this.form.controls.type.value.id,
					country_code: this.form.controls.country.value
				},
				allowedDomains
			});
		}
	}

	public toggleOrgJoinViaEmailDomain () {
		const { value: checked } = this.form.get('orgJoinViaEmailDomain');

		if (!this.orgJoinViaEmailDomainConfirmDialogFlag) {
			const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = this.dialog.open(ConfirmDialogComponent, {
				data: {
					title: this.translatePipe.transform('account-setup-org-join-via-email-domain-confirm-dialog-title'),
					message: this.translatePipe.transform('account-setup-org-join-via-email-domain-confirm-dialog-message'),
					confirmText: this.translatePipe.transform('common_continue')
				} as ConfirmDialogData
			});

			dialogRef.afterClosed()
				.subscribe((confirm) => {
					if (confirm) {
						this.form.controls.orgJoinViaEmailDomain.setValue(!checked);
					}

					this.orgJoinViaEmailDomainConfirmDialogFlag = true;
				});
		} else {
			this.form.controls.orgJoinViaEmailDomain.setValue(!checked);
		}
	}

	public get shouldShowOrgJoinViaEmailDomainCheckbox (): boolean {
		return !this.unallowedDomain;
	}
}
